/**
 * Jobs API
 *
 * Functions for managing scrape jobs.
 *
 * Note: `(supabase as any)` is used for chained table queries until generated
 * `Database` types include all tables (e.g. job_suburb_lists, discovery_tasks).
 */

import { withoutDateWindow } from "@repo/adapter-core/date-window";
import { isScheduledJob, nextCronRunAt } from "@repo/adapter-core/job-lifecycle";
import type { JobConfigFromPromptResult } from "@repo/adapter-definitions/job-config";
import { supabase } from "@/integrations/supabase/client";
import type { CrawlJobConfig, Job, JobRun, JobWithRelations, ScrapeQueueItem } from "./types";

// ============================================================
// Job CRUD Operations
// ============================================================

/**
 * Fetch all jobs with adapter info (supports both single and multi-adapter patterns)
 */
export async function fetchJobs(): Promise<JobWithRelations[]> {
  const { data, error } = await (supabase as any)
    .from("scrape_jobs")
    .select(`
      *,
      adapters(*),
      job_adapters(id, adapter_id, config, mapping_status, scraping_status, adapters(*)),
      job_suburbs(suburb_id, suburbs(*)),
      job_suburb_lists(suburb_list_id, suburb_lists(*))
    `)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching jobs:", error);
    throw error;
  }

  return data || [];
}

/**
 * Fetch a single job by ID with all relations
 */
export async function fetchJobById(jobId: string): Promise<JobWithRelations | null> {
  const { data, error } = await (supabase as any)
    .from("scrape_jobs")
    .select(`
      *,
      adapters(*),
      job_adapters(id, adapter_id, config, mapping_status, scraping_status, adapters(*)),
      job_suburbs(suburb_id, suburbs(*)),
      job_suburb_lists(suburb_list_id, suburb_lists(*))
    `)
    .eq("id", jobId)
    .single();

  if (error) {
    console.error("Error fetching job:", error);
    return null;
  }

  return data;
}

/**
 * Create a new job (legacy single-adapter pattern)
 */
export async function createJob(
  name: string,
  adapterId: string,
  config: CrawlJobConfig,
  runType: "immediate" | "scheduled" = "immediate",
  cronExpression?: string,
): Promise<Job> {
  const {
    data: { user },
  } = await (supabase as any).auth.getUser();

  const nextRunAt =
    runType === "scheduled" && cronExpression ? nextCronRunAt(cronExpression).toISOString() : null;

  const { data, error } = await (supabase as any)
    .from("scrape_jobs")
    .insert({
      name,
      adapter_id: adapterId,
      created_by: user?.id,
      config,
      run_type: runType,
      cron_expression: cronExpression,
      next_run_at: nextRunAt,
      output_format: "json",
      status: runType === "scheduled" ? "scheduled" : "idle",
    })
    .select("*, adapters(*)")
    .single();

  if (error) {
    console.error("Error creating job:", error);
    throw error;
  }

  // Sync cron schedules
  if (data) {
    try {
      await supabase.functions.invoke("sync-cron-schedules", {});
    } catch (syncError) {
      console.error("Error syncing cron schedules:", syncError);
    }
  }

  return data;
}

/**
 * Create a new job with suburbs and adapters (multi-adapter pattern)
 */
export async function createQueueJob(
  name: string,
  suburbIds: string[],
  adapterIds: string[],
  suburbListIds: string[] = [],
  runType: "immediate" | "scheduled" = "immediate",
  cronExpression?: string,
  config?: CrawlJobConfig,
  adapterConfigs?: Record<string, Record<string, unknown>>,
  options?: {
    initialStatus?: Job["status"];
    debugPauseOnSubmit?: boolean;
  },
): Promise<JobWithRelations> {
  const {
    data: { user },
  } = await (supabase as any).auth.getUser();

  const nextRunAt =
    runType === "scheduled" && cronExpression ? nextCronRunAt(cronExpression).toISOString() : null;
  const initialStatus = options?.initialStatus ?? (runType === "scheduled" ? "scheduled" : "idle");

  // Create the job
  const { data: job, error: jobError } = await (supabase as any)
    .from("scrape_jobs")
    .insert({
      name,
      adapter_id: null, // Multi-adapter jobs don't have a single adapter
      created_by: user?.id,
      config: config || {},
      run_type: runType,
      cron_expression: cronExpression,
      next_run_at: nextRunAt,
      output_format: "json",
      status: initialStatus,
    })
    .select()
    .single();

  if (jobError) {
    console.error("Error creating job:", jobError);
    throw jobError;
  }

  // Link suburbs to job
  if (suburbIds.length > 0) {
    const suburbLinks = suburbIds.map((suburbId) => ({
      job_id: job.id,
      suburb_id: suburbId,
    }));

    const { error: suburbError } = await (supabase as any).from("job_suburbs").insert(suburbLinks);

    if (suburbError) {
      console.error("Error linking suburbs to job:", suburbError);
      await (supabase as any).from("scrape_jobs").delete().eq("id", job.id);
      throw suburbError;
    }
  }

  // Link adapters to job with per-adapter configs
  if (suburbListIds.length > 0) {
    const suburbListLinks = suburbListIds.map((suburbListId) => ({
      job_id: job.id,
      suburb_list_id: suburbListId,
      created_by: user?.id ?? null,
    }));

    const { error: suburbListError } = await (supabase as any)
      .from("job_suburb_lists")
      .insert(suburbListLinks);

    if (suburbListError) {
      console.error("Error linking suburb lists to job:", suburbListError);
      await (supabase as any).from("job_suburbs").delete().eq("job_id", job.id);
      await (supabase as any).from("job_suburb_lists").delete().eq("job_id", job.id);
      await (supabase as any).from("scrape_jobs").delete().eq("id", job.id);
      throw suburbListError;
    }
  }

  // Link adapters to job with per-adapter configs
  if (adapterIds.length > 0) {
    const adapterLinks = adapterIds.map((adapterId) => ({
      job_id: job.id,
      adapter_id: adapterId,
      // The date window lives on the job only (one per job); never per adapter.
      config: withoutDateWindow(adapterConfigs?.[adapterId] || {}),
      mapping_status: "pending",
      scraping_status: "pending",
    }));

    const { error: adapterError } = await (supabase as any)
      .from("job_adapters")
      .insert(adapterLinks);

    if (adapterError) {
      console.error("Error linking adapters to job:", adapterError);
      await (supabase as any).from("job_suburbs").delete().eq("job_id", job.id);
      await (supabase as any).from("job_suburb_lists").delete().eq("job_id", job.id);
      await (supabase as any).from("scrape_jobs").delete().eq("id", job.id);
      throw adapterError;
    }
  }

  // Sync cron schedules if scheduled
  if (runType === "scheduled" && cronExpression) {
    try {
      await supabase.functions.invoke("sync-cron-schedules", {});
    } catch (syncError) {
      console.error("Error syncing cron schedules:", syncError);
    }
  }

  // Fetch and return the complete job
  const fullJob = await fetchJobById(job.id);
  if (!fullJob) {
    throw new Error("Failed to fetch created job");
  }

  return fullJob;
}

/**
 * Delete a job
 */
export async function deleteJob(jobId: string): Promise<void> {
  const { error } = await (supabase as any).from("scrape_jobs").delete().eq("id", jobId);

  if (error) {
    console.error("Error deleting job:", error);
    throw error;
  }

  // Sync cron schedules to remove the entry
  try {
    await supabase.functions.invoke("sync-cron-schedules", {});
  } catch (syncError) {
    console.error("Error syncing cron schedules after deletion:", syncError);
  }
}

export interface JobScheduleUpdate {
  runType: Job["run_type"];
  cronExpression: string | null;
  config: CrawlJobConfig;
  suburbListIds: string[];
}

/**
 * Edit a job's schedule, config (date window, crawl policy) and suburb lists after
 * creation, then re-sync pg_cron.
 */
export async function updateJobSchedule(job: Job, update: JobScheduleUpdate): Promise<void> {
  const {
    data: { user },
  } = await (supabase as any).auth.getUser();
  const scheduled = update.runType === "scheduled" && Boolean(update.cronExpression?.trim());
  const currentListIds = (job.job_suburb_lists ?? []).map((row) => row.suburb_list_id);
  const removed = currentListIds.filter((id) => !update.suburbListIds.includes(id));
  const added = update.suburbListIds.filter((id) => !currentListIds.includes(id));

  if (added.length > 0) {
    const { error } = await (supabase as any).from("job_suburb_lists").insert(
      added.map((suburbListId) => ({
        job_id: job.id,
        suburb_list_id: suburbListId,
        created_by: user?.id ?? null,
      })),
    );
    if (error) throw error;
  }
  if (removed.length > 0) {
    const { error } = await (supabase as any)
      .from("job_suburb_lists")
      .delete()
      .eq("job_id", job.id)
      .in("suburb_list_id", removed);
    if (error) throw error;
  }

  // Only a change of run type moves the status, and never mid-run or while paused
  // (a paused job is how an operator keeps the scheduler away from it).
  const statusChange =
    scheduled !== isScheduledJob(job) && !["running", "paused"].includes(job.status);
  const { error } = await (supabase as any)
    .from("scrape_jobs")
    .update({
      run_type: scheduled ? "scheduled" : "immediate",
      cron_expression: scheduled ? update.cronExpression?.trim() : null,
      next_run_at: scheduled ? nextCronRunAt(update.cronExpression ?? "").toISOString() : null,
      config: update.config,
      ...(statusChange ? { status: scheduled ? "scheduled" : "idle" } : {}),
    })
    .eq("id", job.id);
  if (error) throw error;

  try {
    await supabase.functions.invoke("sync-cron-schedules", {});
  } catch (syncError) {
    console.error("Error syncing cron schedules after schedule edit:", syncError);
  }
}

/**
 * Update job status
 */
export async function updateJobStatus(jobId: string, status: Job["status"]): Promise<void> {
  const { error } = await (supabase as any).from("scrape_jobs").update({ status }).eq("id", jobId);

  if (error) {
    console.error("Error updating job status:", error);
    throw error;
  }
}

// ============================================================
// Job Run Operations
// ============================================================

/**
 * Run a job via the job orchestrator edge function. `forceRecrawl` overrides the job's crawl
 * policy for this run only: pages for sales we already hold are fetched again.
 */
export async function runJob(
  jobId: string,
  options: { forceRecrawl?: boolean } = {},
): Promise<{
  success: boolean;
  runId?: string;
  scraped?: number;
  urlsFound?: number;
  errors?: number;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke("job-orchestrator", {
    body: { action: "run-job", jobId, ...options },
  });

  if (error) {
    console.error("Error running job:", error);
    // A non-2xx reply carries the function's own reason in its body; prefer it to the generic message.
    const body = await (error as { context?: Response }).context?.json?.().catch(() => null);
    return { success: false, error: typeof body?.error === "string" ? body.error : error.message };
  }

  return data;
}

/**
 * Turn a plain-English job description into a validated, id-resolved job config
 * (job-config-from-prompt edge function). Nothing is created; the caller lodges the job.
 */
export async function generateJobConfigFromPrompt(
  prompt: string,
  clarifications: { question: string; answer: string }[] = [],
): Promise<JobConfigFromPromptResult> {
  const { data, error } = await supabase.functions.invoke("job-config-from-prompt", {
    body: { prompt, clarifications },
  });
  if (error) {
    const body = await (error as { context?: Response }).context?.json?.().catch(() => null);
    const details = Array.isArray(body?.details) ? ` (${body.details.join("; ")})` : "";
    throw new Error(`${body?.error ?? error.message}${details}`);
  }
  if (!data?.success) throw new Error(data?.error ?? "Job config generation failed");
  return data.result as JobConfigFromPromptResult;
}

/**
 * Cancel a running job run
 */
export async function cancelJobRun(runId: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke("job-orchestrator", {
    body: { action: "cancel-run", runId },
  });

  if (error) {
    console.error("Error cancelling job run:", error);
    return { success: false, error: error.message };
  }

  return data?.success
    ? { success: true }
    : { success: false, error: data?.error || "Failed to cancel" };
}

/**
 * Pause a running job run
 */
export async function pauseJobRun(runId: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke("job-orchestrator", {
    body: { action: "pause-run", runId },
  });

  if (error) {
    console.error("Error pausing job run:", error);
    return { success: false, error: error.message };
  }

  return data?.success
    ? { success: true }
    : { success: false, error: data?.error || "Failed to pause" };
}

/**
 * Resume a paused job run
 */
export async function resumeJobRun(runId: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke("job-orchestrator", {
    body: { action: "resume-run", runId },
  });

  if (error) {
    console.error("Error resuming job run:", error);
    return { success: false, error: error.message };
  }

  return data?.success
    ? { success: true }
    : { success: false, error: data?.error || "Failed to resume" };
}

/**
 * Fetch job runs for a specific job
 */
export async function fetchJobRuns(jobId: string): Promise<JobRun[]> {
  const { data, error } = await (supabase as any)
    .from("job_runs")
    .select("*")
    .eq("job_id", jobId)
    .order("started_at", { ascending: false });

  if (error) {
    console.error("Error fetching job runs:", error);
    throw error;
  }

  return data || [];
}

// ============================================================
// Queue Operations
// ============================================================

/**
 * Fetch queue items for a job
 */
export async function fetchJobQueue(jobId: string): Promise<ScrapeQueueItem[]> {
  const { data, error } = await (supabase as any)
    .from("scrape_queue")
    .select("*")
    .eq("job_id", jobId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error fetching job queue:", error);
    throw error;
  }

  return data || [];
}

/**
 * Get queue stats for a job.
 * Uses individual count queries to avoid Supabase's 1000-row default limit.
 */
export async function getJobQueueStats(jobId: string): Promise<{
  total: number;
  pending: number;
  in_progress: number;
  completed: number;
  failed: number;
}> {
  const statuses = ["pending", "in_progress", "completed", "failed"] as const;

  const [totalResult, ...statusResults] = await Promise.all([
    (supabase as any)
      .from("scrape_queue")
      .select("*", { count: "exact", head: true })
      .eq("job_id", jobId),
    ...statuses.map((status) =>
      (supabase as any)
        .from("scrape_queue")
        .select("*", { count: "exact", head: true })
        .eq("job_id", jobId)
        .eq("status", status),
    ),
  ]);

  if (totalResult.error) {
    console.error("Error fetching queue stats:", totalResult.error);
    throw totalResult.error;
  }

  return {
    total: totalResult.count || 0,
    pending: statusResults[0].count || 0,
    in_progress: statusResults[1].count || 0,
    completed: statusResults[2].count || 0,
    failed: statusResults[3].count || 0,
  };
}

// ============================================================
// Job Suburbs
// ============================================================

/**
 * Suburb with per-job scraping stats
 */
export interface JobSuburbWithStats {
  suburb_id: string;
  suburb_name: string;
  state: string;
  postcode: string;
  region: string | null;
  urls_discovered: number;
  urls_scraped: number;
  urls_failed: number;
}

/**
 * Fetch suburbs for a job with optional per-run queue stats
 */
export async function fetchJobSuburbs(
  jobId: string,
  jobRunId?: string,
): Promise<JobSuburbWithStats[]> {
  // Get the suburbs linked to this job
  const { data: jobSuburbs, error } = await (supabase as any)
    .from("job_suburbs")
    .select("suburb_id, suburbs(id, suburb_name, state, postcode, region)")
    .eq("job_id", jobId);

  if (error || !jobSuburbs) {
    console.error("Error fetching job suburbs:", error);
    return [];
  }

  // Get queue stats grouped by suburb for this job (or specific run)
  // Uses count queries per suburb to avoid Supabase's 1000-row default limit
  const suburbIds = jobSuburbs.map((js: { suburb_id: string }) => js.suburb_id).filter(Boolean);
  const statsMap = new Map<string, { discovered: number; scraped: number; failed: number }>();

  if (suburbIds.length > 0) {
    // Fetch counts per suburb in parallel
    const countPromises = suburbIds.map(async (suburbId: string) => {
      let totalQuery = (supabase as any)
        .from("scrape_queue")
        .select("*", { count: "exact", head: true })
        .eq("job_id", jobId)
        .eq("suburb_id", suburbId);
      let completedQuery = (supabase as any)
        .from("scrape_queue")
        .select("*", { count: "exact", head: true })
        .eq("job_id", jobId)
        .eq("suburb_id", suburbId)
        .eq("status", "completed");
      let failedQuery = (supabase as any)
        .from("scrape_queue")
        .select("*", { count: "exact", head: true })
        .eq("job_id", jobId)
        .eq("suburb_id", suburbId)
        .eq("status", "failed");

      if (jobRunId) {
        totalQuery = totalQuery.eq("job_run_id", jobRunId);
        completedQuery = completedQuery.eq("job_run_id", jobRunId);
        failedQuery = failedQuery.eq("job_run_id", jobRunId);
      }

      const [totalRes, completedRes, failedRes] = await Promise.all([
        totalQuery,
        completedQuery,
        failedQuery,
      ]);

      return {
        suburbId,
        discovered: totalRes.count || 0,
        scraped: completedRes.count || 0,
        failed: failedRes.count || 0,
      };
    });

    const results = await Promise.all(countPromises);
    for (const r of results) {
      statsMap.set(r.suburbId, { discovered: r.discovered, scraped: r.scraped, failed: r.failed });
    }
  }

  let missingSuburbCount = 0;

  const mappedSuburbs = jobSuburbs.map(
    (js: {
      suburb_id: string;
      suburbs?: {
        id: string;
        suburb_name: string;
        state: string;
        postcode: string;
        region: string | null;
      } | null;
    }) => {
      const stats = statsMap.get(js.suburb_id) || { discovered: 0, scraped: 0, failed: 0 };
      const suburb = js.suburbs;
      if (!suburb) missingSuburbCount += 1;

      return {
        suburb_id: js.suburb_id,
        suburb_name: suburb?.suburb_name || `Unknown (${js.suburb_id})`,
        state: suburb?.state || "Unknown",
        postcode: suburb?.postcode || "",
        region: suburb?.region ?? null,
        urls_discovered: stats.discovered,
        urls_scraped: stats.scraped,
        urls_failed: stats.failed,
      };
    },
  );

  if (missingSuburbCount > 0) {
    console.warn(
      `Missing suburb joins for ${missingSuburbCount} rows in job_suburbs (job_id=${jobId})`,
    );
  }

  return mappedSuburbs.sort((a: JobSuburbWithStats, b: JobSuburbWithStats) => {
    // Sort by state then suburb name
    if (a.state !== b.state) return a.state.localeCompare(b.state);
    return a.suburb_name.localeCompare(b.suburb_name);
  });
}

// ============================================================
// Per-(suburb, adapter) breakdown — job-total + current-run scopes
// ============================================================

export interface SuburbAdapterScopeStats {
  /** COUNT(DISTINCT url) — unique URLs discovered for this scope. */
  uniqueUrls: number;
  /** COUNT(DISTINCT url) FILTER WHERE status='completed'. */
  scraped: number;
  /** COUNT(DISTINCT url) FILTER WHERE status='failed'. */
  failed: number;
  /** COUNT(DISTINCT url) FILTER WHERE is_out_of_range=true. */
  outOfRange: number;
}

export interface SuburbAdapterBreakdown {
  adapter_id: string;
  adapter_slug: string;
  adapter_name: string;
  jobTotal: SuburbAdapterScopeStats;
  runTotal: SuburbAdapterScopeStats;
}

export interface SuburbBreakdown {
  suburb_id: string;
  suburb_name: string;
  state: string;
  postcode: string;
  jobTotal: SuburbAdapterScopeStats;
  runTotal: SuburbAdapterScopeStats;
  adapters: SuburbAdapterBreakdown[];
}

/**
 * Per-(suburb, adapter) breakdown using COUNT(DISTINCT url) semantics.
 *
 * Returns two scopes per group:
 *   - jobTotal: every run of this job
 *   - runTotal: just the specified runId (or 0s if no runId)
 *
 * Counts deduplicate by URL because a property may legitimately appear in
 * the queue twice across re-runs — the user wants the *unique* count, not
 * the row count. Implementation aggregates client-side from a single
 * scrape_queue scan; with a hard cap of 100k rows this comfortably covers
 * jobs up to 5y / 50 postcodes before we need a server-side RPC.
 */
export async function fetchJobSuburbAdapterBreakdown(
  jobId: string,
  jobRunId: string | null,
): Promise<SuburbBreakdown[]> {
  const { data, error } = await (supabase as any)
    .from("scrape_queue")
    .select(
      `suburb_id, adapter_id, url, status, job_run_id, is_out_of_range,
       suburbs!scrape_queue_suburb_id_fkey(suburb_name, state, postcode),
       adapters!scrape_queue_adapter_id_fkey(name, slug)`,
    )
    .eq("job_id", jobId)
    .limit(100000);

  if (error || !data) {
    console.error("Error fetching suburb-adapter breakdown:", error);
    return [];
  }

  // Aggregator: tracks distinct URLs per (suburb_id, adapter_id) for each
  // scope. We use Set<string> for dedup; later we materialise to numbers.
  type AdapterBucket = {
    adapter_id: string;
    adapter_slug: string;
    adapter_name: string;
    job: { all: Set<string>; scraped: Set<string>; failed: Set<string>; outOfRange: Set<string> };
    run: { all: Set<string>; scraped: Set<string>; failed: Set<string>; outOfRange: Set<string> };
  };
  type SuburbBucket = {
    suburb_id: string;
    suburb_name: string;
    state: string;
    postcode: string;
    adapters: Map<string, AdapterBucket>;
  };
  const suburbs = new Map<string, SuburbBucket>();

  const makeAdapter = (id: string, slug: string, name: string): AdapterBucket => ({
    adapter_id: id,
    adapter_slug: slug,
    adapter_name: name,
    job: { all: new Set(), scraped: new Set(), failed: new Set(), outOfRange: new Set() },
    run: { all: new Set(), scraped: new Set(), failed: new Set(), outOfRange: new Set() },
  });

  for (const row of data as Array<{
    suburb_id: string | null;
    adapter_id: string | null;
    url: string | null;
    status: string | null;
    job_run_id: string | null;
    is_out_of_range: boolean | null;
    suburbs: { suburb_name: string; state: string; postcode: string } | null;
    adapters: { name: string; slug: string } | null;
  }>) {
    if (!row.suburb_id || !row.adapter_id || !row.url) continue;
    const suburb = suburbs.get(row.suburb_id) ?? {
      suburb_id: row.suburb_id,
      suburb_name: row.suburbs?.suburb_name ?? "Unknown",
      state: row.suburbs?.state ?? "??",
      postcode: row.suburbs?.postcode ?? "",
      adapters: new Map<string, AdapterBucket>(),
    };
    suburbs.set(row.suburb_id, suburb);

    const adapter =
      suburb.adapters.get(row.adapter_id) ??
      makeAdapter(row.adapter_id, row.adapters?.slug ?? "unknown", row.adapters?.name ?? "Unknown");
    suburb.adapters.set(row.adapter_id, adapter);

    adapter.job.all.add(row.url);
    if (row.status === "completed") adapter.job.scraped.add(row.url);
    if (row.status === "failed") adapter.job.failed.add(row.url);
    if (row.is_out_of_range === true) adapter.job.outOfRange.add(row.url);

    if (jobRunId && row.job_run_id === jobRunId) {
      adapter.run.all.add(row.url);
      if (row.status === "completed") adapter.run.scraped.add(row.url);
      if (row.status === "failed") adapter.run.failed.add(row.url);
      if (row.is_out_of_range === true) adapter.run.outOfRange.add(row.url);
    }
  }

  const toScope = (b: AdapterBucket["job"]): SuburbAdapterScopeStats => ({
    uniqueUrls: b.all.size,
    scraped: b.scraped.size,
    failed: b.failed.size,
    outOfRange: b.outOfRange.size,
  });

  const result: SuburbBreakdown[] = [];
  for (const suburb of suburbs.values()) {
    const adapters: SuburbAdapterBreakdown[] = [];
    const aggregator = {
      job: {
        all: new Set<string>(),
        scraped: new Set<string>(),
        failed: new Set<string>(),
        outOfRange: new Set<string>(),
      },
      run: {
        all: new Set<string>(),
        scraped: new Set<string>(),
        failed: new Set<string>(),
        outOfRange: new Set<string>(),
      },
    };
    for (const adapter of suburb.adapters.values()) {
      adapters.push({
        adapter_id: adapter.adapter_id,
        adapter_slug: adapter.adapter_slug,
        adapter_name: adapter.adapter_name,
        jobTotal: toScope(adapter.job),
        runTotal: toScope(adapter.run),
      });
      for (const u of adapter.job.all) aggregator.job.all.add(u);
      for (const u of adapter.job.scraped) aggregator.job.scraped.add(u);
      for (const u of adapter.job.failed) aggregator.job.failed.add(u);
      for (const u of adapter.job.outOfRange) aggregator.job.outOfRange.add(u);
      for (const u of adapter.run.all) aggregator.run.all.add(u);
      for (const u of adapter.run.scraped) aggregator.run.scraped.add(u);
      for (const u of adapter.run.failed) aggregator.run.failed.add(u);
      for (const u of adapter.run.outOfRange) aggregator.run.outOfRange.add(u);
    }
    adapters.sort((a, b) => b.jobTotal.uniqueUrls - a.jobTotal.uniqueUrls);
    result.push({
      suburb_id: suburb.suburb_id,
      suburb_name: suburb.suburb_name,
      state: suburb.state,
      postcode: suburb.postcode,
      jobTotal: toScope(aggregator.job),
      runTotal: toScope(aggregator.run),
      adapters,
    });
  }

  result.sort((a, b) => {
    if (a.state !== b.state) return a.state.localeCompare(b.state);
    return a.suburb_name.localeCompare(b.suburb_name);
  });
  return result;
}

/**
 * Discovery task info for UI display
 */
export interface DiscoveryTaskInfo {
  id: string;
  suburb_name: string;
  state: string;
  adapter_name: string;
  adapter_slug: string;
  /** The lane the task searches: buy | sold | rent (discovery_tasks.listing_type). */
  listing_type: string;
  status: string;
  search_url: string | null;
  current_page: number;
  urls_found: number;
  urls_raw_total: number;
  urls_in_suburb_total: number;
  urls_new_total: number;
  urls_out_of_scope_total: number;
  consecutive_empty_pages: number;
  error_message: string | null;
  updated_at: string | null;
  config: Record<string, unknown>;
}

/**
 * Fetch discovery tasks for a specific job run. Selects every column (*) rather than naming
 * listing_type: the dashboard can deploy before migration 20261023090000 adds it, and a missing
 * listing_type reads as sold.
 */
export async function fetchJobDiscoveryTasks(jobRunId: string): Promise<DiscoveryTaskInfo[]> {
  const { data, error } = await (supabase as any)
    .from("discovery_tasks")
    .select(`
      *,
      suburbs!discovery_tasks_suburb_id_fkey(suburb_name, state),
      adapters!discovery_tasks_adapter_id_fkey(name, slug)
    `)
    .eq("job_run_id", jobRunId)
    .order("created_at", { ascending: true });

  if (error || !data) {
    console.error("Error fetching discovery tasks:", error);
    return [];
  }

  return data.map(
    (dt: {
      id: string;
      status: string;
      listing_type: string | null;
      search_url: string | null;
      current_page: number;
      urls_found: number;
      urls_raw_total: number | null;
      urls_in_suburb_total: number | null;
      urls_new_total: number | null;
      urls_out_of_scope_total: number | null;
      consecutive_empty_pages: number | null;
      error_message: string | null;
      updated_at: string | null;
      config: Record<string, unknown>;
      suburbs: { suburb_name: string; state: string } | null;
      adapters: { name: string; slug: string } | null;
    }) => ({
      id: dt.id,
      suburb_name: dt.suburbs?.suburb_name || "Unknown",
      state: dt.suburbs?.state || "??",
      adapter_name: dt.adapters?.name || "Unknown",
      adapter_slug: dt.adapters?.slug || "unknown",
      listing_type: dt.listing_type ?? "sold",
      status: dt.status,
      search_url: dt.search_url,
      current_page: dt.current_page,
      urls_found: dt.urls_found,
      urls_raw_total: dt.urls_raw_total ?? 0,
      urls_in_suburb_total: dt.urls_in_suburb_total ?? 0,
      urls_new_total: dt.urls_new_total ?? 0,
      urls_out_of_scope_total: dt.urls_out_of_scope_total ?? 0,
      consecutive_empty_pages: dt.consecutive_empty_pages ?? 0,
      error_message: dt.error_message,
      updated_at: dt.updated_at,
      config: dt.config,
    }),
  );
}

// ============================================================
// Export Operations
// ============================================================

export interface SnapshotExportFilters {
  suburb?: string;
  postcodes?: string[];
  state?: string;
  adapterSlug?: string;
  provider?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
}

export interface SnapshotExportOptions {
  exportName?: string;
  chunkSizeRows?: number;
  selectedColumns?: string[];
}

export async function runSnapshotProvenanceExport(options?: {
  format?: "json" | "csv";
  filters?: SnapshotExportFilters;
  exportOptions?: SnapshotExportOptions;
}): Promise<{
  success: boolean;
  exportId?: string;
  fileBucket?: string;
  filePath?: string;
  filePaths?: string[];
  chunkCount?: number;
  chunkSizeRows?: number;
  selectedColumns?: string[];
  rowCount?: number;
  snapshotCount?: number;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke("export-business-intel", {
    body: {
      action: "run-export",
      format: options?.format || "json",
      filters: options?.filters || {},
      exportOptions: options?.exportOptions || {},
    },
  });

  if (error) {
    console.error("Error running snapshot provenance export:", error);
    return { success: false, error: error.message };
  }

  return data;
}

export async function listSnapshotExports(): Promise<
  Array<{
    id: string;
    status: string;
    format: string;
    file_bucket: string | null;
    file_path: string | null;
    manifest_path: string | null;
    export_name: string | null;
    selected_columns: string[] | null;
    chunk_size_rows: number | null;
    chunk_count: number | null;
    file_size_bytes: number | null;
    metadata: Record<string, unknown> | null;
    row_count: number;
    created_at: string;
    completed_at: string | null;
    error_message: string | null;
  }>
> {
  const { data, error } = await supabase.functions.invoke("export-business-intel", {
    body: {
      action: "list-exports",
    },
  });

  if (error) {
    console.error("Error listing snapshot exports:", error);
    return [];
  }

  return data?.exports || [];
}
